import React, { useEffect, useState } from 'react';
// v3 because `BaseAction.inputSchema` is typed against it.
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { Input } from '@cotera/client/v0/components/ui/input';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { ResourceType } from '../resources/selection/resource-selection.model';
import { isWarehouseDataset } from './dataset-utils';
import { DatasetModel } from './dataset.model';

const setWriteBackTableParamsSchema = z.object({
  /** The table to write back to, or null to use the one the query reads. */
  table: z
    .object({ schema: z.string().min(1), name: z.string().min(1) })
    .nullable(),
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof setWriteBackTableParamsSchema>;

type Errors = { t: string; message?: string }[];

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type SetWriteBackTableActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

/**
 * Names the warehouse table a warehouse dataset's workflow write-backs land in.
 *
 * A plain `SELECT … FROM schema.table` already writes back to the table it
 * reads, so this is only needed when the query has no single table — a join or
 * an aggregate — or the output belongs somewhere else. The server checks the
 * table exists and carries the dataset's row-key columns before saving it.
 */
export class SetWriteBackTableAction extends BaseAction<Params, Errors> {
  title = 'Write-back table';
  description = 'Choose the warehouse table workflow write-backs land in';
  inputSchema = setWriteBackTableParamsSchema;
  group = 'Dataset';
  icon = 'circle-stack';

  constructor(
    private readonly client: SetWriteBackTableActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    const result = await this.client.resource.dataset.updateSettings({
      id: datasetId,
      writeBackTable: payload.table,
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to save the write-back table'
          : 'Failed to save the write-back table';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });
    toast.success(
      payload.table === null
        ? 'Writing back to the table the query reads'
        : `Writing back to ${payload.table.schema}.${payload.table.name}`
    );
    return ok({});
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    // Resolved before the modal opens: the selection can move while it is up.
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <SetWriteBackTableAskView
              datasetId={datasetId}
              onRespond={async (nextParams) => {
                await onRespond({
                  ...nextParams,
                  ...(datasetId !== null ? { datasetId } : {}),
                });
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}

/**
 * Opens on the table already saved, so a user who only came to look does not
 * overwrite it with a blank.
 */
const SetWriteBackTableAskView: React.FC<{
  datasetId: string | null;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, onRespond, onCancel }) => {
  const client = useTenantedClient();
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [useQueryTable, setUseQueryTable] = useState(true);
  const [schema, setSchema] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (datasetId === null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await client.resource.get({
        type: ResourceType.Dataset,
        id: datasetId,
      });
      if (cancelled) {
        return;
      }
      if (
        res.isErr() ||
        res.value.type !== 'dataset' ||
        !isWarehouseDataset(res.value.dataset.type)
      ) {
        setLoadError('Only warehouse datasets write back to a table');
        return;
      }
      const saved = res.value.dataset.writeBackTable ?? null;
      setUseQueryTable(saved === null);
      setSchema(saved?.schema ?? '');
      setName(saved?.name ?? '');
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId]);

  const submittable =
    useQueryTable || (schema.trim() !== '' && name.trim() !== '');
  const canSubmit = !saving && loaded && datasetId !== null && submittable;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setSaving(true);
    await onRespond({
      table: useQueryTable
        ? null
        : { schema: schema.trim(), name: name.trim() },
    });
    setSaving(false);
  };

  return (
    <ModalPageView
      title="Write-back table"
      subtitle="Where workflow write-backs to this dataset land in your warehouse"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        {loadError !== null ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : !loaded ? (
          <div className="flex justify-center py-6">
            <Loading.Spinner variant="sm" />
          </div>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted/40">
              <Checkbox
                checked={useQueryTable}
                onCheckedChange={() => setUseQueryTable((current) => !current)}
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium">
                  Use the table the query reads
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  only when the query is a plain select from one table
                </span>
              </span>
            </label>

            {useQueryTable ? (
              <span className="text-xs text-muted-foreground">
                A query that joins or aggregates has no single table to write
                to, so nothing is written back until you name one.
              </span>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Schema"
                    value={schema}
                    onChange={(event) => setSchema(event.target.value)}
                  />
                  <Input
                    placeholder="Table"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  Exactly as the warehouse stores it — Snowflake keeps unquoted
                  names in upper case. The table needs this dataset&rsquo;s
                  row-key columns: each write-back finds its row by that key,
                  and adds a row when there isn&rsquo;t one.
                </span>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {saving ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};
